// lib/ai-converter.ts
import { OpenAI } from 'openai';

// 创建DeepSeek客户端
const openai = new OpenAI({
  apiKey: process.env.DEEPSEEK_API_KEY || '',
  baseURL: 'https://api.deepseek.com',
  dangerouslyAllowBrowser:true
});

export type Framework = 'html' | 'react' | 'vue' | 'uniapp';

// 转换提示词模板（更新为普通CSS）
const conversionTemplates: Record<string, string> = {
  'html_to_react': `你是一个React专家。将下面的HTML+CSS代码转换为React组件代码。

要求：
1. 使用函数组件和JSX语法
2. 将class属性改为className
3. 将onclick等事件改为onClick（驼峰命名）
4. 将<style>标签内的CSS转换为CSS模块或内联样式
5. 如果CSS较多，建议创建单独的.css文件
6. 确保组件是独立的，可以直接导入使用
7. 只返回代码，不要解释

HTML+CSS代码：
{sourceCode}

React组件代码：`,

  'html_to_vue': `你是一个Vue专家。将下面的HTML+CSS代码转换为Vue 3单文件组件。

要求：
1. 使用Vue 3组合式API（<script setup>）
2. 保持相同的UI效果
3. 将CSS转换为Vue的<style scoped>标签内
4. 确保组件是独立的，可以直接使用
5. 只返回代码，不要解释

HTML+CSS代码：
{sourceCode}

Vue组件代码：`,

'html_to_uniapp': `# 任务说明
你是专业的uni-app开发者，需要将HTML+CSS代码转换为Vue 2风格的uni-app页面。

# 严格规则（必须遵守）：
1. **只输出代码**：不要有任何文字说明、注释、解释、功能列表、设计说明等
2. **不要包含**：##功能说明、设计特点、交互功能等任何描述性文字
3. **格式要求**：必须是完整的Vue单文件组件，包含且仅包含三个部分：
   - <template>标签及内容
   - <script>标签及内容  
   - <style lang="scss" scoped>标签及内容
4. **代码要求**：
   - 移除所有HTML文档标签（<!DOCTYPE>, <html>, <head>, <body>等）
   - 不要包含markdown代码块标记（如\`\`\`）
   - 不要包含任何中文说明文字
   - 不要包含"功能说明"、"设计特点"等段落

# 转换要求：
1. **组件结构**：使用Vue 2选项式API（export default { data(), methods: {} }）
2. **标签转换**：
   - <div> → <view>
   - <span>/<p>/<h1-6> → <text>
   - <img> → <image mode="widthFix">
   - <a> → <navigator> 或 <view>
   - <form> → <form>
   - <input>/<button>保持不变
3. **事件转换**：
   - onclick/onclick → @tap
   - onchange → @change
   - oninput → @input
4. **样式转换**：
   - px转换为rpx（1px = 2rpx）
   - CSS变量(:root)转换为SCSS变量($variable)
   - :hover替换为.hover-class
5. **数据提取**：
   - 根据输入框自动生成data()中的响应式数据
   - 根据事件自动生成methods中的方法

# 输入代码：
{sourceCode}

# 输出格式（必须是这个结构，没有其他内容）：
<template>
  <view class="page-container">
    <!-- 转换后的视图内容 -->
  </view>
</template>

<script>
export default {
  data() {
    return {
      // 自动提取的表单数据
    }
  },
  methods: {
    // 自动提取的方法
  }
}
</script>

<style lang="scss" scoped>
// 转换后的样式
</style>`,

  'react_to_html': `将下面的React组件代码转换为HTML+CSS代码。

要求：
1. 将JSX转换为普通HTML
2. 将className改回class
3. 将onClick等事件改回onclick
4. 将React样式转换为普通CSS（放在<style>标签内）
5. 确保HTML可以在浏览器中直接运行
6. 只返回代码，不要解释

React代码：
{sourceCode}

HTML+CSS代码：`,

  'vue_to_html': `将下面的Vue组件代码转换为HTML+CSS代码。

要求：
1. 移除Vue特定语法（v-bind、v-on等）
2. 转换为普通HTML
3. 将Vue的<style scoped>内容转换为普通CSS
4. 确保HTML可以在浏览器中直接运行
5. 只返回代码，不要解释

Vue代码：
{sourceCode}

HTML+CSS代码：`,

  'uniapp_to_html': `将下面的uni-app代码转换为HTML+CSS代码。

要求：
1. 将uni-app组件（<view>等）转换为普通HTML标签
2. 移除uni-app特定语法
3. 将SCSS转换为普通CSS
4. 将rpx单位转换为px（1rpx ≈ 0.5px）
5. 确保HTML可以在浏览器中直接运行
6. 只返回代码，不要解释

uni-app代码：
{sourceCode}

HTML+CSS代码：`
};

// 主要的AI转换函数
export async function aiConvert(
  sourceCode: string,
  sourceFramework: Framework,
  targetFramework: Framework
): Promise<string> {
  try {
    console.log(`AI转换请求: ${sourceFramework} → ${targetFramework}`);
    
    // 构建转换键
    const conversionKey = `${sourceFramework}_to_${targetFramework}`;
    const template = conversionTemplates[conversionKey];
    
    if (!template) {
      throw new Error(`不支持的转换类型: ${sourceFramework} → ${targetFramework}`);
    }
    // 在 aiConvert 函数中调用：
    if (targetFramework === 'uniapp') {
      const convertedCode = await aiConvertRaw(sourceCode, sourceFramework, targetFramework);
      return postProcessUniappCode(convertedCode);
    }
    // 准备提示词
    const prompt = template.replace('{sourceCode}', sourceCode);
    
    // 调用DeepSeek API
    const response = await openai.chat.completions.create({
      model: 'deepseek-chat',
      messages: [
        {
          role: 'system',
          content: '你是一个专业的前端代码转换专家。请严格按照要求进行代码转换，只返回转换后的代码，不要添加任何解释、注释或额外文本。'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 5000, // 增加token数量
      temperature: 0.1,
      stream: false
    });

    const convertedCode = response.choices[0]?.message?.content?.trim();
    
    if (!convertedCode) {
      throw new Error('AI返回的代码为空');
    }

    console.log(`AI转换完成: ${sourceFramework} → ${targetFramework} (${convertedCode.length}字符)`);
    
    // 清理可能的额外内容
    return cleanAiResponse(convertedCode);
    
  } catch (error: any) {
    console.error('AI转换错误:', error);
    
    if (error.message?.includes('API key')) {
      throw new Error('请配置正确的DeepSeek API Key');
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      throw new Error('网络连接失败，请检查网络设置');
    } else if (error.message?.includes('rate limit')) {
      throw new Error('API调用频率限制，请稍后重试');
    }
    
    throw new Error(`AI转换失败: ${error.message || '未知错误'}`);
  }
}




// 验证代码格式
export function validateCodeFormat(code: string, framework: Framework): boolean {
  if (!code || code.trim().length === 0) {
    return false;
  }
  
  switch (framework) {
    case 'html':
      return code.includes('<') && code.includes('>');
    case 'react':
      return code.includes('React') || code.includes('JSX') || code.includes('className');
    case 'vue':
      return code.includes('<template>') || code.includes('<script') || code.includes('Vue');
    case 'uniapp':
      return code.includes('<view>') || code.includes('uni-app') || code.includes('小程序');
    default:
      return true;
  }
}


// 在 lib/ai-converter.ts 中添加

/**
 * 原始AI转换函数（不进行后处理）
 */
async function aiConvertRaw(
  sourceCode: string,
  sourceFramework: string,
  targetFramework: string
): Promise<string> {
  try {
    console.log(`原始AI转换请求: ${sourceFramework} → ${targetFramework}`);
    
    // 构建转换键
    const conversionKey = `${sourceFramework}_to_${targetFramework}`;
    const template = conversionTemplates[conversionKey];
    
    if (!template) {
      throw new Error(`不支持的转换类型: ${sourceFramework} → ${targetFramework}`);
    }

    // 准备提示词
    const prompt = template.replace('{sourceCode}', sourceCode);
    
    // 调用DeepSeek API
    const response = await openai.chat.completions.create({
      model: 'deepseek-chat',
      messages: [
        {
          role: 'system',
          content: '你是一个专业的前端代码转换专家。请严格按照要求进行代码转换，只返回转换后的代码，不要添加任何解释、注释或额外文本。确保输出的是完整的、可运行的代码。'
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      max_tokens: 5000,
      temperature: 0.1,
      stream: false
    });

    const convertedCode = response.choices[0]?.message?.content?.trim();
    
    if (!convertedCode) {
      throw new Error('AI返回的代码为空');
    }

    console.log(`原始AI转换完成: ${sourceFramework} → ${targetFramework} (${convertedCode.length}字符)`);
    
    return convertedCode;
    
  } catch (error: any) {
    console.error('原始AI转换错误:', error);
    
    if (error.message?.includes('API key')) {
      throw new Error('请配置正确的DeepSeek API Key');
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      throw new Error('网络连接失败，请检查网络设置');
    } else if (error.message?.includes('rate limit')) {
      throw new Error('API调用频率限制，请稍后重试');
    }
    
    throw new Error(`AI转换失败: ${error.message || '未知错误'}`);
  }
}



// 添加清理函数（现有的 cleanAiResponse 保持不变，但可以优化）
function cleanAiResponse(code: string): string {
  // 移除可能的代码块标记
  let cleaned = code
    .replace(/```[\w]*\n/g, '')
    .replace(/```$/g, '')
    .replace(/^\s*```\s*$/gm, '')
    .trim();
  
  // 移除常见的AI解释文本
  const explanationPatterns = [
    // 英文解释
    /^Of course[.,].*?code:\s*/i,
    /^Here['’]s.*?code:\s*/i,
    /^The.*?code.*?:\s*/i,
    /^Following.*?:\s*/i,
    /^Converted.*?:\s*/i,
    /^Here is.*?:\s*/i,
    // 中文解释
    /^当然[，,].*?代码[：:]\s*/,
    /^以下[是]?.*?代码[：:]\s*/,
    /^转换.*?代码[：:]\s*/,
    /^根据.*?要求[，,].*?[：:]\s*/,
    /^我已经.*?转换.*?[：:]\s*/,
    /^这里.*?转换.*?代码[：:]\s*/,
    /^请看.*?转换结果[：:]\s*/,
    /^这是.*?代码[：:]\s*/,
    // 设计说明等
    /设计说明[：:].*?\n/,
    /这个.*?具有以下特点.*?\n/,
    /界面.*?特点.*?\n/,
    /我将设计.*?\n/,
    /采用.*?风格.*?\n/,
    /[\u4e00-\u9fa5]+[\s\S]*?(?=<template>|$)/, // 所有中文内容直到 template 开始
  ];
  
  explanationPatterns.forEach(pattern => {
    cleaned = cleaned.replace(pattern, '');
  });
  
  // 移除多余的空行
  cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  return cleaned.trim();
}

// 更新批量转换函数，使用新的结构
export async function batchConvert(
  sourceCode: string,
  sourceFramework: Framework,
  targetFrameworks: Framework[]
): Promise<Record<Framework, string>> {
  const results: Record<Framework, string> = {} as any;
  
  for (const targetFramework of targetFrameworks) {
    if (targetFramework === sourceFramework) {
      results[targetFramework] = sourceCode;
      continue;
    }
    
    try {
      const result = await aiConvert(sourceCode, sourceFramework, targetFramework);
      results[targetFramework] = result;
    } catch (error) {
      console.error(`批量转换失败 ${targetFramework}:`, error);
      
      // 提供更友好的错误信息
      let errorMessage = error instanceof Error ? error.message : '未知错误';
      
      // 如果是uni-app转换失败，提供降级方案
      if (targetFramework === 'uniapp') {
        errorMessage = 'uni-app转换失败，请检查HTML结构是否复杂';
      }
      
      results[targetFramework] = `<!-- 转换到 ${targetFramework} 失败: ${errorMessage} -->`;
    }
  }
  
  return results;
}


// 添加 uni-app 后处理函数
export function postProcessUniappCode(code: string): string {
  console.log('开始后处理 uni-app 代码');
  
  let processed = code;
  
  // 1. 移除所有HTML文档结构
  const unwantedPatterns = [
    /<!DOCTYPE[^>]*>/gi,
    /<html[^>]*>/gi,
    /<\/html>/gi,
    /<head[^>]*>[\s\S]*?<\/head>/gi,
    /<body[^>]*>/gi,
    /<\/body>/gi,
    /```html[\s\S]*?```/gi,
    /```vue[\s\S]*?```/gi,
    /```[\s\S]*?```/gi, // 通用代码块
  ];
  
  unwantedPatterns.forEach(pattern => {
    processed = processed.replace(pattern, '');
  });
  
  // 2. 确保是完整的Vue单文件组件
  const hasTemplate = processed.includes('<template>');
  const hasScript = processed.includes('<script>');
  const hasStyle = processed.includes('<style>');
  
  if (!hasTemplate || !hasScript || !hasStyle) {
    console.log('检测到不完整的 uni-app 代码，进行修复');
    
    // 尝试提取现有部分
    const templateMatch = processed.match(/<template>([\s\S]*?)<\/template>/);
    const scriptMatch = processed.match(/<script>([\s\S]*?)<\/script>/);
    const styleMatch = processed.match(/<style[^>]*>([\s\S]*?)<\/style>/);
    
    // 提取视图内容
    let viewContent = '';
    if (templateMatch) {
      viewContent = templateMatch[1];
    } else {
      // 如果没有template，尝试提取view内容
      const viewMatch = processed.match(/<view[^>]*>[\s\S]*?<\/view>/);
      if (viewMatch) {
        viewContent = viewMatch[0];
      } else {
        // 最后尝试：把整个内容作为view
        viewContent = processed;
      }
    }
    
    // 构建完整的组件
    processed = `<template>
  <view class="page-container">
    ${viewContent}
  </view>
</template>

<script>
export default {
  data() {
    return {
      phone: '',
      password: '',
      isLoading: false
    }
  },
  methods: {
    
  },
  onLoad() {
    console.log('页面加载');
  }
}
</script>

<style lang="scss" scoped>
${styleMatch ? styleMatch[1] : ''}
</style>`;
  }
  
  // 3. 清理CSS变量引用（如果还有）
  processed = processed.replace(/var\(--[^)]+\)/g, (match) => {
    // 尝试提取变量名并替换为硬编码值
    const varName = match.match(/--([^)]+)/)?.[1];
    if (varName) {
      // 常见变量的默认值
      const defaultValues: Record<string, string> = {
        'primary-color': '#2c5aa0',
        'primary-light': '#4a7bc1',
        'secondary-color': '#d4af37',
        'background-color': '#f8f9fa',
        'text-color': '#333333',
        'text-light': '#666666',
        'border-color': '#e0e0e0',
        'white': '#ffffff',
        'shadow': '0 8rpx 24rpx rgba(0, 0, 0, 0.08)',
        'border-radius': '24rpx',
        'transition': 'all 0.3s ease'
      };
      
      return defaultValues[varName] || '#2c5aa0';
    }
    return '#2c5aa0';
  });
  
  // 4. 清理多余空行和空格
  processed = processed
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .replace(/^\s+$/gm, '')
    .trim();
  
  console.log('uni-app 代码后处理完成');
  return processed;
}